import Actions from "../actions";
import { SettingsAction } from "./types";
import { AppThunkAction } from "..";
import {
    getSettingsAsync,
    updateGeneralSettingsAsync,
    updateReportsSettingsAsync,
    updateRemindersSettingsAsync,
    updatePaymentsSettingsAsync,
    updateExtrasSettingsAsync,
    updateClientsLoginSettingsAsync,
    updateIntegrationsSettingsAsync,
    updateAlertsSettingsAsync,
    updateTaskManagementSettingsAsync,
    updateClientsTagsSettingsAsync,
    updateInvoicesSettingsAsync,
    updateConsentManagementSettingsAsync,
    updateCashRegisterSettingsAsync,
    updateWaitingListSettingsAsync,
    updatePrescriptionsSettingsAsync,
    getInvoiceCatalogAsync,
    getTaxRatesAsync,
    getTaxExemptionsAsync,
    getReportTemplatesAsync,
    getReportTemplateAsync,
    getSurveysAsync,
    getPackTemplatesAsync,
    getPackTemplateAsync,
    getReportTemplateCatalogAsync,
} from "../../ajax/requests";
import { getErrorMessage } from "../../ajax/errors";
import { alertError, alertGenericError, alertSuccess } from "../alerts/actions";
import { TK } from "../translations/translationKeys";
import {
    SettingsData,
    GeneralSettingsData,
    ReportsSettingsData,
    RemindersSettingsData,
    PaymentsSettingsData,
    ExtrasSettingsData,
    ClientsLoginSettingsData,
    IntegrationsSettingsData,
    AlertsSettingsData,
    InvoiceSettingsData,
    ConsentManagementSettingsData,
    CashRegisterSettings,
    WaitingListSettingsData,
    PrescriptionsSettingsData,
    InvoiceCatalogData,
    TaxRate,
    TaxExemption,
    FeatureEnum,
    TaskManagementSettingsData,
} from "../../models";
import { CatalogCategoryData, ReportTemplateData } from "../../models/api-models";
import { SurveySettingsData } from "../../shared/surveys";
import { PackSettings } from "../../models";
import { translate } from "../translations/hooks";
import { getMyUser } from "../session/selectors";

const settingsLoaded = (settings: SettingsData): SettingsAction => ({
    type: Actions.SettingsLoaded,
    payload: settings,
});

const settingsUpdated = (settings: SettingsData): SettingsAction => ({
    type: Actions.SettingsUpdated,
    payload: settings,
});

const invoiceCatalogLoading = () => ({
    type: Actions.InvoiceCatalogLoading,
});

const invoiceCatalogLoaded = (catalog?: InvoiceCatalogData) => ({
    type: Actions.InvoiceCatalogLoaded,
    payload: catalog,
});

const taxRatesLoading = () => ({
    type: Actions.TaxRatesLoading,
});

const taxRatesLoaded = (rates?: TaxRate[]) => ({
    type: Actions.TaxRatesLoaded,
    payload: rates,
});

const taxExemptionsLoading = () => ({
    type: Actions.TaxExemptionsLoading,
});

const taxExemptionsLoaded = (exemptions?: TaxExemption[]) => ({
    type: Actions.TaxExemptionsLoaded,
    payload: exemptions,
});

const reportTemplatesLoading = () => ({
    type: Actions.ReportTemplatesLoading,
});

const reportTemplatesLoaded = (templates: ReportTemplateData[], fetchedAt: number) => ({
    type: Actions.ReportTemplatesLoaded,
    payload: { templates, fetchedAt },
});

const surveysLoading = () => ({
    type: Actions.SurveysLoading,
});

const surveysLoaded = (surveys: SurveySettingsData[]) => ({
    type: Actions.SurveysLoaded,
    payload: surveys,
});

const invoiceCatalogCleared = () => ({
    type: Actions.InvoiceCatalogCleared,
});

export const loadSettings = () : AppThunkAction<Promise<SettingsData>> => {
    return async (dispatch) => {
        const settings = await getSettingsAsync();
        dispatch(settingsLoaded(settings));
        return settings;
    };
};

export const updateGeneralSettings = (general: GeneralSettingsData) : AppThunkAction<Promise<void>> => {
    return async (dispatch, getState) => {
        try {
            const state = getState();

            await updateGeneralSettingsAsync(general);

            // Reload settings from API to ensure Redux has the correct data
            const updatedSettings = await getSettingsAsync();
            dispatch(settingsUpdated(updatedSettings));

            // Clear invoice catalog cache since services may have changed
            dispatch(invoiceCatalogCleared());

            dispatch(alertSuccess(translate(TK.settingsUpdated)));
        }
        catch(error) {
            const errorData = (error as { response?: { data?: { message?: string; Message?: string } } })?.response?.data;
            const message = errorData?.message || errorData?.Message;
            if (typeof message === 'string' && message.trim().length) {
                dispatch(alertError(message));
            }
            else {
                dispatch(alertGenericError());
            }
        }
    };
};

export const loadInvoiceCatalog = (): AppThunkAction<Promise<void>> => {
    return async (dispatch, getState) => {
        const state = getState();

        if (state.settings.invoiceCatalogLoading) {
            return;
        }

        dispatch(invoiceCatalogLoading());

        try {
            const catalog = await getInvoiceCatalogAsync();
            dispatch(invoiceCatalogLoaded(catalog));
        }
        catch (error) {
            dispatch(invoiceCatalogLoaded());
            const message = getErrorMessage(error);
            if (typeof message === "string" && message.trim().length) {
                dispatch(alertError(message));
            } else {
                dispatch(alertGenericError());
            }
        }
    };
};

export const loadTaxRates = (): AppThunkAction<Promise<void>> => {
    return async (dispatch, getState) => {
        const state = getState();
        const user = getMyUser(state);
        if (!user?.features.includes(FeatureEnum.Invoices)) {
            return;
        }
        // Return if already loading or already cached
        if (state.settings.taxRatesLoading || state.settings.taxRates) {
            return;
        }

        dispatch(taxRatesLoading());

        try {
            const rates = await getTaxRatesAsync();
            dispatch(taxRatesLoaded(rates));
        }
        catch (error) {
            // On error, dispatch empty array to prevent retrying on every render
            dispatch(taxRatesLoaded([]));
            const message = getErrorMessage(error);
            if (typeof message === 'string' && message.trim().length) {
                dispatch(alertError(message));
            } else {
                dispatch(alertGenericError());
            }
        }
    };
};

export const loadTaxExemptions = (): AppThunkAction<Promise<void>> => {
    return async (dispatch, getState) => {
        const state = getState();
        // Return if already loading or already cached
        const user = getMyUser(state);
        if (!user?.features.includes(FeatureEnum.Invoices)) {
            return;
        }
        if (state.settings.taxExemptionsLoading || state.settings.taxExemptions) {
            return;
        }

        dispatch(taxExemptionsLoading());

        try {
            const exemptions = await getTaxExemptionsAsync();
            dispatch(taxExemptionsLoaded(exemptions));
        }
        catch (error) {
            // On error, dispatch empty array to prevent retrying on every render
            dispatch(taxExemptionsLoaded([]));
            const message = getErrorMessage(error);
            if (typeof message === 'string' && message.trim().length) {
                dispatch(alertError(message));
            } else {
                dispatch(alertGenericError());
            }
        }
    };
};

export const loadSurveys = (options?: { force?: boolean; silent?: boolean }): AppThunkAction<Promise<SurveySettingsData[]>> => {
    return async (dispatch, getState) => {
        const { force = false, silent = true } = options ?? {};
        const state = getState();
        const user = getMyUser(state);

        if (!user?.features.includes(FeatureEnum.Surveys)) {
            return [];
        }

        if (!force) {
            if (state.settings.surveysLoading) {
                return state.settings.surveys ?? [];
            }

            if (state.settings.surveys) {
                return state.settings.surveys;
            }
        }

        dispatch(surveysLoading());

        try {
            const surveys = await getSurveysAsync();
            const result = surveys ?? [];
            dispatch(surveysLoaded(result));
            return result;
        } catch (error) {
            dispatch(surveysLoaded([]));

            if (!silent) {
                const message = getErrorMessage(error);
                if (typeof message === "string" && message.trim().length) {
                    dispatch(alertError(message));
                } else {
                    dispatch(alertGenericError());
                }
            }

            return [];
        }
    };
};

export const refreshSurveys = (options?: { silent?: boolean }): AppThunkAction<Promise<SurveySettingsData[]>> =>
    loadSurveys({ force: true, silent: options?.silent ?? true });

const REPORT_TEMPLATES_STALE_MS = 5 * 60 * 1000;

export const loadReportTemplates = (force?: boolean): AppThunkAction<Promise<void>> => {
    return async (dispatch, getState) => {
        const state = getState();
        if (state.settings.reportTemplatesLoading) return;

        if (!force) {
            const fetchedAt = state.settings.reportTemplatesFetchedAt;
            if (fetchedAt && Date.now() - fetchedAt < REPORT_TEMPLATES_STALE_MS) return;
        }

        dispatch(reportTemplatesLoading());

        try {
            const templates = await getReportTemplatesAsync();
            dispatch(reportTemplatesLoaded(templates, Date.now()));
        }
        catch (error) {
            dispatch(reportTemplatesLoaded([], Date.now()));
            const message = getErrorMessage(error);
            if (typeof message === 'string' && message.trim().length) {
                dispatch(alertError(message));
            } else {
                dispatch(alertGenericError());
            }
        }
    };
};

export const loadReportTemplateVersion = (templateId: string, version: number): AppThunkAction<Promise<void>> => {
    return async (dispatch, getState) => {
        const state = getState();
        const alreadyLoaded = state.settings.reportTemplates?.some(
            t => t.templateId === templateId && t.version === version
        );
        if (alreadyLoaded) return;

        try {
            const template = await getReportTemplateAsync(templateId, version);
            dispatch({ type: Actions.ReportTemplateVersionLoaded, payload: template });
        } catch {
            // version not found — nothing to store
        }
    };
};

const PACK_TEMPLATES_STALE_MS = 5 * 60 * 1000;

const packTemplatesLoading = () => ({
    type: Actions.PackTemplatesLoading,
});

const packTemplatesLoaded = (templates: PackSettings[], fetchedAt: number) => ({
    type: Actions.PackTemplatesLoaded,
    payload: { templates, fetchedAt },
});

export const loadPackTemplates = (force?: boolean): AppThunkAction<Promise<void>> => {
    return async (dispatch, getState) => {
        const state = getState();
        if (state.settings.packTemplatesLoading) return;

        if (!force) {
            const fetchedAt = state.settings.packTemplatesFetchedAt;
            if (fetchedAt && Date.now() - fetchedAt < PACK_TEMPLATES_STALE_MS) return;
        }

        dispatch(packTemplatesLoading());

        try {
            const templates = await getPackTemplatesAsync();
            dispatch(packTemplatesLoaded(templates, Date.now()));
        }
        catch (error) {
            dispatch(packTemplatesLoaded([], Date.now()));
            const message = getErrorMessage(error);
            if (typeof message === 'string' && message.trim().length) {
                dispatch(alertError(message));
            } else {
                dispatch(alertGenericError());
            }
        }
    };
};

const REPORT_TEMPLATE_CATALOG_STALE_MS = 5 * 60 * 1000;

const reportTemplateCatalogLoading = () => ({
    type: Actions.ReportTemplateCatalogLoading,
});

const reportTemplateCatalogLoaded = (catalog: CatalogCategoryData[], fetchedAt: number) => ({
    type: Actions.ReportTemplateCatalogLoaded,
    payload: { catalog, fetchedAt },
});

export const loadReportTemplateCatalog = (force?: boolean): AppThunkAction<Promise<void>> => {
    return async (dispatch, getState) => {
        const state = getState();
        if (state.settings.reportTemplateCatalogLoading) return;

        if (!force) {
            const fetchedAt = state.settings.reportTemplateCatalogFetchedAt;
            if (fetchedAt && Date.now() - fetchedAt < REPORT_TEMPLATE_CATALOG_STALE_MS) return;
        }

        dispatch(reportTemplateCatalogLoading());

        try {
            const catalog = await getReportTemplateCatalogAsync();
            dispatch(reportTemplateCatalogLoaded(catalog, Date.now()));
        }
        catch (error) {
            dispatch(reportTemplateCatalogLoaded([], Date.now()));
            const message = getErrorMessage(error);
            if (typeof message === 'string' && message.trim().length) {
                dispatch(alertError(message));
            } else {
                dispatch(alertGenericError());
            }
        }
    };
};

export const loadPackTemplateVersion = (templateId: string, version: number): AppThunkAction<Promise<void>> => {
    return async (dispatch, getState) => {
        const state = getState();
        const alreadyLoaded = state.settings.packTemplates?.some(
            t => t.templateId === templateId && t.version === version
        );
        if (alreadyLoaded) return;

        try {
            const template = await getPackTemplateAsync(templateId, version);
            dispatch({ type: Actions.PackTemplateVersionLoaded, payload: template });
        } catch {
            // version not found — nothing to store
        }
    };
};

export const updateGeneralClientsTagsSettings = (clientTags: GeneralSettingsData['clientTags']) : AppThunkAction<Promise<void>> => {
    return async (dispatch, getState) => {
        try {
            const state = getState();

            await updateClientsTagsSettingsAsync(clientTags);

            dispatch(settingsUpdated({...state.settings, general: {...state.settings.general, clientTags}}));
            dispatch(alertSuccess(translate(TK.settingsUpdated)));
        }
        catch(error) {
            dispatch(alertGenericError());
        } 
    };
};

export const updateExtrasSettings = (extras: ExtrasSettingsData) : AppThunkAction<Promise<void>> => {
    return async (dispatch, getState) => {
        try {
            const state = getState();

            await updateExtrasSettingsAsync(extras);

            dispatch(settingsUpdated({...state.settings, extras}));

            try {
                const updatedSettings = await getSettingsAsync();
                dispatch(settingsUpdated(updatedSettings));
            }
            catch {
                // Ignore refresh failures; optimistic state already updated
            }

            // Clear invoice catalog cache since extras may have changed
            dispatch(invoiceCatalogCleared());

            dispatch(alertSuccess(translate(TK.settingsUpdated)));
        }
        catch(error) {
            dispatch(alertGenericError());
        }
    };
};

export const updateReportsSettings = (settings: ReportsSettingsData) : AppThunkAction<Promise<void>> => {
    return async (dispatch, getState) => {
        try {
            const state = getState();

            await updateReportsSettingsAsync(settings);

            dispatch(settingsUpdated({...state.settings, reports: settings}));
            dispatch(alertSuccess(translate(TK.settingsUpdated)));
        }
        catch(error) {
            dispatch(alertGenericError());
        } 
    };
};

export const updateRemindersSettings = (reminders: RemindersSettingsData) : AppThunkAction<Promise<void>> => {
    return async (dispatch) => {
        try {
            await updateRemindersSettingsAsync(reminders);

            const updatedSettings = await getSettingsAsync();
            dispatch(settingsUpdated(updatedSettings));
            dispatch(alertSuccess(translate(TK.settingsUpdated)));
        }
        catch(error) {
            const errorData = (error as { response?: { data?: { message?: string; Message?: string } } })?.response?.data;
            const message = errorData?.message || errorData?.Message;
            if (typeof message === 'string' && message.trim().length) {
                dispatch(alertError(message));
            }
            else {
                dispatch(alertGenericError());
            }
        } 
    };
};

export const updateTaskManagementSettings = (taskManagement: TaskManagementSettingsData): AppThunkAction<Promise<void>> => {
    return async (dispatch, getState) => {
        try {
            const state = getState();

            await updateTaskManagementSettingsAsync(taskManagement);

            dispatch(settingsUpdated({
                ...state.settings,
                taskManagement,
            }));
            dispatch(alertSuccess(translate(TK.settingsUpdated)));
        }
        catch (error) {
            dispatch(alertGenericError());
        }
    };
};


export const updateAlertsSettings = (alerts: AlertsSettingsData) : AppThunkAction<Promise<void>> => {
    return async (dispatch, getState) => {
        try {
            const state = getState();

            await updateAlertsSettingsAsync(alerts);

            dispatch(settingsUpdated({...state.settings, alerts}));
            dispatch(alertSuccess(translate(TK.settingsUpdated)));
        }
        catch(error) {
            dispatch(alertGenericError());
        } 
    };
};

export const updatePaymentsSettings = (settings: PaymentsSettingsData) : AppThunkAction<Promise<void>> => {
    return async (dispatch, getState) => {
        try {
            const state = getState();

            await updatePaymentsSettingsAsync({
                ...settings,
                profiles: settings.profiles.filter(r => !settings.profiles.some(o => o.id === r.id && o.version > r.version)),
            });

            dispatch(settingsUpdated({...state.settings, payments: settings}));
            dispatch(alertSuccess(translate(TK.settingsUpdated)));
        }
        catch(error) {
            dispatch(alertGenericError());
        } 
    };
};

export const updateClientsLoginSettings = (settings: ClientsLoginSettingsData) : AppThunkAction<Promise<void>> => {
    return async (dispatch, getState) => {
        try {
            const state = getState();

            await updateClientsLoginSettingsAsync(settings);

            dispatch(settingsUpdated({...state.settings, clientsLogin: settings}));
            dispatch(alertSuccess(translate(TK.settingsUpdated)));
        }
        catch(error) {
            dispatch(alertGenericError());
        } 
    };
};

export const updateIntegrationsSettings = (settings: IntegrationsSettingsData) : AppThunkAction<Promise<void>> => {
    return async (dispatch, getState) => {
        try {
            const state = getState();

            await updateIntegrationsSettingsAsync(settings);

            dispatch(settingsUpdated({...state.settings, integrations: settings}));
            dispatch(alertSuccess(translate(TK.settingsUpdated)));
        }
        catch(error) {
            dispatch(alertGenericError());
        } 
    };
};

export const updateInvoicesSettings = (settings: InvoiceSettingsData) : AppThunkAction<Promise<void>> => {
    return async (dispatch, getState) => {
        const state = getState();

        // Optimistically update state immediately so user doesn't lose their input
        dispatch(settingsUpdated({...state.settings, invoices: settings}));

        try {
            const response = await updateInvoicesSettingsAsync(settings);

            // Refresh from server to get any server-side computed values
            try {
                const updatedSettings = await getSettingsAsync();
                dispatch(settingsUpdated(updatedSettings));
            }
            catch {
                // Ignore refresh failures; optimistic state already updated
            }

            // Show the message returned by the backend (always present now)
            const message = (response as any)?.message || (response as any)?.Message;
            if (typeof message === 'string' && message.trim().length) {
                dispatch(alertSuccess(message));
            }
            else {
                dispatch(alertSuccess(translate(TK.settingsUpdated)));
            }
        }
        catch(error) {
            // Keep the optimistic update even on error, since backend now saves settings
            // This allows users to fix only the problematic fields without re-entering everything
            const errorData = (error as { response?: { data?: { message?: string; Message?: string } } })?.response?.data;
            const message = errorData?.message || errorData?.Message;
            if (typeof message === 'string' && message.trim().length) {
                dispatch(alertError(message));
            }
            else {
                dispatch(alertGenericError());
            }
        }
    };
};

export const updateConsentManagementSettings = (settings: ConsentManagementSettingsData) : AppThunkAction<Promise<void>> => {
    return async (dispatch, getState) => {
        const state = getState();

        // Optimistically update state
        dispatch(settingsUpdated({...state.settings, consentManagement: settings}));

        try {
            await updateConsentManagementSettingsAsync(settings);

            // Refresh from server
            try {
                const updatedSettings = await getSettingsAsync();
                dispatch(settingsUpdated(updatedSettings));
            }
            catch {
                // Ignore refresh failures
            }

            dispatch(alertSuccess(translate(TK.settingsUpdated)));
        }
        catch(error) {
            const errorData = (error as { response?: { data?: { message?: string; Message?: string } } })?.response?.data;
            const message = errorData?.message || errorData?.Message;
            if (typeof message === 'string' && message.trim().length) {
                dispatch(alertError(message));
            }
            else {
                dispatch(alertGenericError());
            }
        }
    };
};

export const updateCashRegisterSettings = (settings: CashRegisterSettings) : AppThunkAction<Promise<void>> => {
    return async (dispatch, getState) => {
        const state = getState();

        // Optimistically update state
        dispatch(settingsUpdated({...state.settings, cashRegisterSettings: settings}));

        try {
            await updateCashRegisterSettingsAsync(settings);

            // Refresh from server
            try {
                const updatedSettings = await getSettingsAsync();
                dispatch(settingsUpdated(updatedSettings));
            }
            catch {
                // Ignore refresh failures
            }

            dispatch(alertSuccess(translate(TK.settingsUpdated)));
        }
        catch(error) {
            const errorData = (error as { response?: { data?: { message?: string; Message?: string } } })?.response?.data;
            const message = errorData?.message || errorData?.Message;
            if (typeof message === 'string' && message.trim().length) {
                dispatch(alertError(message));
            }
            else {
                dispatch(alertGenericError());
            }
        }
    };
};

export const updateWaitingListSettings = (settings: WaitingListSettingsData) : AppThunkAction<Promise<void>> => {
    return async (dispatch, getState) => {
        try {
            const state = getState();

            await updateWaitingListSettingsAsync(settings);

            dispatch(settingsUpdated({...state.settings, waitingListSettings: settings}));
            dispatch(alertSuccess(translate(TK.settingsUpdated)));
        }
        catch(error) {
            dispatch(alertGenericError());
        }
    };
};

export const updatePrescriptionsSettings = (settings: PrescriptionsSettingsData) : AppThunkAction<Promise<void>> => {
    return async (dispatch, getState) => {
        try {
            const state = getState();

            await updatePrescriptionsSettingsAsync(settings);

            dispatch(settingsUpdated({...state.settings, prescriptionsSettings: settings}));
            dispatch(alertSuccess(translate(TK.settingsUpdated)));
        }
        catch(error) {
            dispatch(alertGenericError());
        }
    };
};
